import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AssignedCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [message, setMessage] = useState('');
  const [selectedCallType, setSelectedCallType] = useState('');
  const [selectedTaskType, setSelectedTaskType] = useState('');

  // Fetch staff members on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const staffResponse = await axios.get('https://astrovidhya.in/api/staff');
        setStaffList(staffResponse.data);
      } catch (error) {
        console.error('Error fetching staff:', error);
        setMessage('Failed to fetch staff members.');
      }
    };

    fetchData();
  }, []);

  // Fetch customers based on selected call type and task type
  const fetchCustomers = async () => {
    if (!selectedCallType) {
      setMessage('Please select a Call Type.');
      return;
    }

    try {
      const response = await axios.get('https://astrovidhya.in/api/assigned-customers', {
        params: {
          callType: selectedCallType,
          taskType: selectedTaskType,
        },
      });
      setCustomers(response.data);
      setFilteredCustomers(response.data); // Initialize filtered list
      setMessage('Customers loaded successfully.');
    } catch (error) {
      console.error('Error fetching customers:', error);
      setMessage('Failed to fetch customers.');
    }
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (customerId) => {
    setSelectedCustomers((prevSelected) =>
      prevSelected.includes(customerId)
        ? prevSelected.filter((id) => id !== customerId)
        : [...prevSelected, customerId]
    );
  };

  // Assign selected tasks to a staff member
  const assignTasks = async () => {
    if (!selectedStaff || selectedCustomers.length === 0) {
      setMessage('Please select tasks and a staff member.');
      return;
    }

    try {
      const response = await axios.post('https://astrovidhya.in/api/reassign-tasks', {
        tasks: selectedCustomers,
        staffId: selectedStaff,
      });
      setMessage(response.data.message);
      setSelectedCustomers([]); // Clear selected customers
      fetchCustomers(); // Refresh customers
    } catch (error) {
      console.error('Error assigning tasks:', error);
      setMessage('Failed to assign tasks.');
    }
  };

  // Handle staff selection from dropdown to filter customers
  const handleStaffFilter = (staffId) => {
    setSelectedStaff(staffId);
    const filtered = customers.filter((customer) => customer.assignedTo?._id === staffId);
    setFilteredCustomers(filtered);
  };

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">Reassign Tasks</h2>

      <div className="bg-white p-6 rounded-lg shadow-md">
        {/* Call Type Dropdown */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Call Type</label>
          <select
            value={selectedCallType}
            onChange={(e) => setSelectedCallType(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 w-full"
          >
            <option value="">Select Call Type</option>
            <option value="Assigned">Assigned</option>
            <option value="NCR">NCR</option>
            <option value="NRW">NRW</option>
            <option value="CMB">CMB</option>
            <option value="NI">NI</option>
            <option value="CC">CC</option>
            <option value="Demo">Demo</option>
          </select>
        </div>

        {/* Task Type Radio Buttons */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2"></label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="Order"
                checked={selectedTaskType === 'Order'}
                onChange={(e) => setSelectedTaskType(e.target.value)}
                className="form-radio accent-[#03C9D7] text-blue-500"
              />
              <span className="ml-2">Order</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="Subscription"
                checked={selectedTaskType === 'Subscription'}
                onChange={(e) => setSelectedTaskType(e.target.value)}
                className="form-radio accent-[#03C9D7] text-blue-500"
              />
              <span className="ml-2">Subscription</span>
            </label>
          </div>
        </div>

        {/* Fetch Customers Button */}
        <div className="mb-4">
          <button
            onClick={fetchCustomers}
            className="bg-[#03c9d7] hover:bg-[#02A5B3] text-white px-4 py-2 rounded-lg w-1/6"
          >
            Fetch Tasks
          </button>
        </div>

        {/* Staff Dropdown for Filtering */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Filter by Staff</label>
          <select
            value={selectedStaff}
            onChange={(e) => handleStaffFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 w-full"
          >
            <option value="">Select a Staff Member</option>
            {staffList.map((staff) => (
              <option key={staff._id} value={staff._id}>
                {staff.name}
              </option>
            ))}
          </select>
        </div>

        {/* Assign Task Dropdown */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Assign To</label>
          <select
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 w-full"
          >
            <option value="">Select a Staff Member</option>
            {staffList.map((staff) => (
              <option key={staff._id} value={staff._id}>
                {staff.name}
              </option>
            ))}
          </select>
        </div>

        {/* Assign Task Button */}
        <div className="mb-4">
          <button
            onClick={assignTasks}
            className={`px-4 py-2 rounded-lg ${
              selectedCustomers.length > 0 && selectedStaff
                ? 'bg-[#028ca3] hover:bg-[#02A5B3] text-white px-4 py-2 rounded-lg w-1/6'
                : 'bg-[#028ca3] hover:bg-[#02A5B3] text-white px-4 py-2 rounded-lg w-1/6'
            }`}
            disabled={!selectedStaff || selectedCustomers.length === 0}
          >
            Assign Task
          </button>
        </div>


        {/* Customers Table */}
        <table className="w-full border-collapse border border-gray-300 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2">
                <input
                  type="checkbox"
                  onChange={(e) =>
                    setSelectedCustomers(
                      e.target.checked ? customers.map((c) => c._id) : []
                    )
                  }
                />
              </th>
              <th className="border border-gray-300 px-4 py-2">Date</th>
              <th className="border border-gray-300 px-4 py-2">Name</th>
              <th className="border border-gray-300 px-4 py-2">Assigned To</th>
              <th className="border border-gray-300 px-4 py-2">Task Type</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer._id} className="hover:bg-gray-100">
                <td className="border border-gray-300 px-4 py-2">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.includes(customer._id)}
                    onChange={() => handleCheckboxChange(customer._id)}
                  />
                </td>
                <td className="border border-gray-300 px-4 py-2">{customer.date}</td>
                <td className="border border-gray-300 px-4 py-2">{customer.name}</td>
                <td className="border border-gray-300 px-4 py-2">
                  {customer.assignedTo?.name || 'Unassigned'}
                </td>
                <td className="border border-gray-300 px-4 py-2">{customer.type}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {message && <p className="mt-4 text-center text-green-600 font-medium">{message}</p>}
      </div>
    </div>
  );
};

export default AssignedCustomers;
